import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import User from "@insite/mobius/Icons/User";
// import Menu, { MappedLink } from "@insite/mobius/Menu"; // Husky Custom Code
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

/* Husky Custom Code - Start */
import CartLinkComponent, { CartLinkStyles } from "@husky-hrc/Components/CartLink";
import SignIn from "@husky-hrc/Components/Icons/SignIn";
import loadExternalProvidersWithReturn from "@husky-hrc/Framework/Handlers/Data/SignIn/LoadExternalProviders";
import { getExternalProvidersByUrl } from "@husky-hrc/Framework/Store/Data/SignIn/SignInSelector";
import ExtendedApplicationState from "@husky-hrc/Framework/Store/ExtendedApplicationState";
import Menu, { MappedLink, MenuItemStyle, MenuStyle } from "@husky-hrc/MobiusOverrides/Menu/Menu";
import { redirectToAuthProviderUrl } from "@husky-hrc/Utilities/HuskyExternalAuth";
import { moveFocus } from "@husky-hrc/Utilities/Utilities";
import { removeAbsoluteUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { useState } from "react";
/* Husky Custom Code - End */

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: ExtendedApplicationState) => {
    const currentLocation = getLocation(state);
    const returnUrl = removeAbsoluteUrl(currentLocation.pathname);
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // Husky Custom Code
        firstName: state.context.session.firstName,
        externalProvidersState: getExternalProvidersByUrl(state, returnUrl),
        returnUrl,
        isSigningIn: state.context.isSigningIn,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // Husky Custom Code
    loadExternalProvidersWithReturn,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // Husky Custom Code
    cartLinkStyles?: CartLinkStyles;
    signInOutIcon?: IconPresentationProps;
    mobileTitleIcon?: IconPresentationProps;
    mobileTitleClickable?: ClickablePresentationProps;
    mobileCartLinkStyles?: CartLinkStyles;
    hiddenContainerStyles?: HiddenProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
        `,
    },
    titleClickable: {
        css: css`
            padding: 20px 0;
        `,
    },
    titleIcon: {
        size: 52,
        css: css`
            background: ${getColor("primary")};
            border-radius: 50%;
            justify-content: center;
            > svg {
                height: 22px;
                width: 22px;
            }
            transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
            &:hover {
                background-color: ${getColor("primary.contrast")};
                border-color: ${getColor("primary.contrast")};
            }
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            font-size: 14px;
            font-weight: 800;
            line-height: 17px;
        `,
        ellipsis: true,
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
    // Husky Custom Code
    signInOutIcon: {
        size: 16,
        css: css`
            margin-left: 10px;
        `,
    },
    cartLinkStyles: {
        clickableLink: {
            css: css`
                position: relative;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            margin-left: 15px;
                        `,
                        css`
                            margin-left: 50px;
                        `,
                    ])}
            `,
        },
    },
    mobileTitleIcon: {
        size: 30,
        css: css`
            background: ${getColor("primary")};
            border-radius: 50%;
            justify-content: center;
            > svg {
                height: 11px;
                width: 11px;
            }
            transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
            :hover {
                background-color: ${getColor("primary.contrast")};
                border-color: ${getColor("primary.contrast")};
            }
        `,
    },
    mobileTitleClickable: {
        css: css`
            padding: 0;
        `,
    },
    mobileCartLinkStyles: {
        clickableLink: {
            css: css`
                position: relative;
                margin-left: 15px;
            `,
        },
    },
    hiddenContainerStyles: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // Husky Custom Code
    firstName,
    isSigningIn,
    externalProvidersState,
    loadExternalProvidersWithReturn,
    returnUrl,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const [openMenu, setIsOpen] = useState(false);

    useEffect(() => {
        if (!userName && !externalProvidersState.value && !externalProvidersState.isLoading) {
            loadExternalProvidersWithReturn({ returnUrl });
        }
    }, [returnUrl]);

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";

    if (fields.icon === "User") {
        icon = SignIn;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = myAccountPageLink?.children;
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if (!userName || currentUserIsGuest) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                history.push(
                    `${signInUrl}?returnUrl=${currentLocation.pathname}${currentLocation.search}&clientRedirect=true`,
                );
            }
        }
        e.preventDefault();
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const onKeyDownHandler = (e: React.KeyboardEvent) => {
        const key = e.nativeEvent.code.toLowerCase();
        const navItemSelector = "[data-test-selector='mainNavigationItem_signIn']";
        const allChildItems = [...document.querySelectorAll(`${navItemSelector} ${MenuItemStyle}`)] as HTMLElement[];
        const childItems = allChildItems.slice(0, allChildItems.length / 2);

        if (key === "enter" || key === "space") {
            if (key === "space") {
                e.preventDefault();
            }
            setIsOpen(!openMenu);
        } else if ((key === "arrowdown" || key === "arrowup") && openMenu) {
            e.preventDefault();
            moveFocus(key, childItems, true);
        } else if ((key === "tab" || key === "escape") && openMenu) {
            setIsOpen(false);
        } else {
            moveFocus(e.key, childItems, true);
        }
    };

    const onBlurHandler = (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node) && openMenu) {
            setIsOpen(false);
        }
    };

    const handleMenuLinkClick = (e: React.MouseEvent) => {
        setIsOpen(!openMenu);
    };

    const handleMenuHover = () => {
        setIsOpen(true);
    };

    const handleMenuMouseOut = (e: React.MouseEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node) && openMenu) {
            setIsOpen(false);
        }
    };

    let signInStatusText = translate("Sign In / Register");
    let accountMenuItems: MappedLink[] = [];
    if (userName && !currentUserIsGuest) {
        signInStatusText = `${translate("Hello")}, ${firstName}`;
        accountMenuItems = [
            {
                title: translate("My Profile"),
                url: "/your-profile",
            },
            {
                title: translate("My Orders"),
                url: "/MyAccount/Orders",
            },
            {
                title: translate("My Lists"),
                url: "/MyAccount/MyLists",
            },
            {
                title: translate("Sign Out"),
                onClickFunction: () => {
                    signOut();
                },
            },
        ];
    } else if (isSigningIn) {
        signInStatusText = translate("Signing in...");
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            <Hidden below="lg" {...styles.hiddenContainerStyles}>
                {(!userName || (userName && currentUserIsGuest)) && (
                    <Menu
                        data-test-selector="mainNavigationItem_signIn"
                        descriptionId="accountMenu"
                        menuItems={accountMenuItems}
                        maxDepth={1}
                        cssOverrides={{
                            menuItem: css`
                                a:hover {
                                    background-color: ${getColor("common.accent")};
                                }
                            `,
                        }}
                        menuTrigger={
                            <Clickable
                                {...styles.titleClickable}
                                data-test-selector="header_signIn"
                                onClick={() =>
                                    redirectToAuthProviderUrl(externalProvidersState.value!.externalProviders)
                                }
                            >
                                {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                                {showLabel && (
                                    <Typography {...styles.titleTypography}>{translate(signInStatusText)}</Typography>
                                )}
                            </Clickable>
                        }
                        width={200}
                    />
                )}
                {userName && !currentUserIsGuest && (
                    <Menu
                        data-test-selector="mainNavigationItem_signIn"
                        onMouseOver={handleMenuHover}
                        onMouseOut={handleMenuMouseOut}
                        onBlur={onBlurHandler}
                        onKeyDown={onKeyDownHandler}
                        descriptionId="accountMenu"
                        menuItems={accountMenuItems}
                        maxDepth={1}
                        cssOverrides={{
                            menu: css`
                                border-top: 4px solid ${getColor("danger.main")};
                                ${openMenu ? "display: block" : "display: none"}
                            `,
                            menuItem: css`
                                a {
                                    display: flex;
                                }
                                a > span {
                                    padding: 0;
                                    margin: 10px 15px 10px 20px;
                                }
                                a:hover {
                                    background-color: ${getColor("common.accent")};
                                    span {
                                        border-left: 4px solid ${getColor("danger.main")};
                                        padding-left: 8px;
                                        color: ${getColor("danger.main")};
                                    }
                                }
                            `,
                            wrapper: css`
                                ${openMenu
                                    ? `
                                    > ${MenuStyle} {
                                        display: block !important;
                                    }
                                `
                                    : `
                                    > ${MenuStyle} {
                                        display: none !important;
                                    }
                                `}
                            `,
                        }}
                        menuTrigger={
                            <Clickable
                                {...styles.titleClickable}
                                data-test-selector="header_signIn"
                                onClick={handleMenuLinkClick}
                                aria-expanded={openMenu}
                                aria-label={translate(
                                    openMenu
                                        ? `${translate("My Account")} - ${translate("Collapse Menu")}`
                                        : `${translate("My Account")} - ${translate("Expand Menu")}`,
                                )}
                            >
                                {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                                {showLabel && (
                                    <Typography {...styles.titleTypography}>{translate(signInStatusText)}</Typography>
                                )}
                                <Icon src={ChevronDown} {...styles.signInOutIcon} />
                            </Clickable>
                        }
                        width={200}
                    />
                )}
                {userName && !currentUserIsGuest && (
                    <StyledWrapper>
                        <CartLinkComponent extendedStyles={styles.cartLinkStyles} />
                    </StyledWrapper>
                )}
            </Hidden>
            <Hidden above="md" {...styles.hiddenContainerStyles}>
                {userName && !currentUserIsGuest && (
                    <StyledWrapper>
                        <CartLinkComponent extendedStyles={styles.mobileCartLinkStyles} />
                    </StyledWrapper>
                )}
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
